import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Clock, Route } from "lucide-react";
import { buyLink } from "@/data";
import { servicesForCity } from "@/services";

export default function LocalityModal({ locality, image, region, onClose }) {
    const [view, setView] = useState("services"); // "services" | "route"
    const [dir, setDir] = useState(null);

    useEffect(() => {
        setView("services");
        setDir(null);
    }, [locality]);

    useEffect(() => {
        if (!locality) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [locality, onClose]);

    const services = locality ? servicesForCity(locality) : [];

    const openRoute = (d) => {
        setDir(d);
        setView("route");
    };

    return (
        <AnimatePresence>
            {locality && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
                    data-testid="locality-modal"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        data-testid="locality-modal-overlay"
                        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                        className="relative z-10 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl"
                    >
                        {/* Header con imagen */}
                        <div className="relative h-40 sm:h-48 shrink-0 overflow-hidden">
                            <img
                                src={image}
                                alt={locality}
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10" />
                            <button
                                onClick={onClose}
                                data-testid="locality-modal-close"
                                aria-label="Cerrar"
                                className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-ink transition-colors hover:bg-white"
                            >
                                <X className="h-5 w-5" />
                            </button>
                            <div className="absolute inset-x-0 bottom-0 p-6">
                                {region && (
                                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">
                                        {region}
                                    </span>
                                )}
                                <h3 className="font-display text-3xl font-extrabold uppercase leading-none text-white">
                                    {locality}
                                </h3>
                                <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-white/80">
                                    Viajá desde {locality}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex min-h-0 flex-1 flex-col">
                            <AnimatePresence mode="wait" initial={false}>
                                {view === "services" ? (
                                    <motion.div
                                        key="services"
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -12 }}
                                        transition={{ duration: 0.2 }}
                                        className="flex min-h-0 flex-1 flex-col p-6"
                                    >
                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/60">
                                            Servicios disponibles
                                        </p>
                                        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
                                            {services.map((d, i) => (
                                                <div
                                                    key={`${d.serviceId}-${d.from}-${d.to}-${i}`}
                                                    data-testid={`service-${d.from}-${d.to}`}
                                                    className="flex items-center justify-between gap-4 rounded-2xl border border-black/5 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                                                >
                                                    <div className="min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <p className="text-base font-extrabold uppercase tracking-tight text-ink">
                                                                {d.from}{" "}
                                                                <ArrowRight className="inline h-4 w-4 text-brand" />{" "}
                                                                {d.to}
                                                            </p>
                                                            {d.upcoming && (
                                                                <span
                                                                    data-testid="service-upcoming-badge"
                                                                    className="rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                                                                >
                                                                    Próximamente
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!d.upcoming && d.departure && (
                                                            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-ink/60">
                                                                <Clock className="h-3.5 w-3.5 text-brand" />
                                                                Salida: {d.departure} hs
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openRoute(d)}
                                                        data-testid={`view-route-${d.from}-${d.to}`}
                                                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand"
                                                    >
                                                        Ver recorrido
                                                        <ArrowRight className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                            {services.length === 0 && (
                                                <p className="py-6 text-center text-sm text-ink/50">
                                                    Sin servicios disponibles.
                                                </p>
                                            )}
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="route"
                                        initial={{ opacity: 0, x: 12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 12 }}
                                        transition={{ duration: 0.2 }}
                                        className="flex min-h-0 flex-1 flex-col p-6"
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <button
                                                type="button"
                                                onClick={() => setView("services")}
                                                data-testid="back-to-services"
                                                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink/70 transition-colors hover:text-brand"
                                            >
                                                <ArrowLeft className="h-4 w-4" />
                                                Volver a servicios
                                            </button>
                                            <a
                                                href={buyLink(dir?.to)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="route-buy"
                                                className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-dark"
                                            >
                                                Buscar pasaje
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </a>
                                        </div>

                                        <div className="mt-4 flex items-center gap-2">
                                            <Route className="h-4 w-4 text-brand" />
                                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/60">
                                                Recorrido completo
                                            </p>
                                        </div>
                                        <p className="mt-1 text-base font-extrabold uppercase tracking-tight text-ink">
                                            {dir?.from} → {dir?.to}
                                        </p>

                                        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
                                            <ol className="relative ml-1 border-l-2 border-brand/25">
                                                {dir?.stops.map((s, i) => {
                                                    const first = i === 0;
                                                    const last = i === dir.stops.length - 1;
                                                    return (
                                                        <li
                                                            key={`${s.name}-${i}`}
                                                            data-testid={`route-stop-${i}`}
                                                            className="relative flex items-center justify-between gap-3 py-2.5 pl-6"
                                                        >
                                                            <span
                                                                className={`absolute -left-[7px] h-3 w-3 rounded-full ring-4 ring-white ${
                                                                    first || last
                                                                        ? "bg-brand"
                                                                        : "bg-brand/40"
                                                                }`}
                                                            />
                                                            <span
                                                                className={`truncate text-sm ${
                                                                    first || last
                                                                        ? "font-extrabold uppercase tracking-tight text-ink"
                                                                        : "font-medium text-ink/80"
                                                                }`}
                                                            >
                                                                {s.name}
                                                            </span>
                                                            {s.time && (
                                                                <span className="shrink-0 text-xs font-semibold text-ink/50">
                                                                    {s.time} hs
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ol>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
